export interface OrderForShipmentActions {
  id: string;
  status?: string;
  shippingStatus?: string;
  shiprocketOrderId?: string;
  shiprocketShipmentId?: string;
  awbCode?: string;
  pickupStatus?: string;
  labelUrl?: string;
  invoiceUrl?: string;
}

export interface ActionResult {
  allowed: boolean;
  reason?: string;
}

export interface ShipmentActionsMatrix {
  canCreateShipment: ActionResult;
  canAssignAwb: ActionResult;
  canSchedulePickup: ActionResult;
  canCancelShipment: ActionResult;
  canTrackShipment: ActionResult;
  canDownloadLabel: ActionResult;
  canDownloadInvoice: ActionResult;
  canFullFulfill: ActionResult;
}

export function getShipmentActions(order: OrderForShipmentActions | null | undefined): ShipmentActionsMatrix {
  if (!order) {
    const disabled: ActionResult = { allowed: false, reason: 'Order data unavailable' };
    return {
      canCreateShipment: disabled,
      canAssignAwb: disabled,
      canSchedulePickup: disabled,
      canCancelShipment: disabled,
      canTrackShipment: disabled,
      canDownloadLabel: disabled,
      canDownloadInvoice: disabled,
      canFullFulfill: disabled,
    };
  }

  const isOrderCancelled = (order.status || '').toUpperCase() === 'CANCELLED';
  const isShipmentCancelled = (order.shippingStatus || '').toUpperCase() === 'CANCELLED';
  const isCancelled = isOrderCancelled || isShipmentCancelled;

  const isDelivered = (order.status || '').toUpperCase() === 'DELIVERED' || (order.shippingStatus || '').toUpperCase() === 'DELIVERED';
  const hasShipmentId = Boolean(order.shiprocketShipmentId);
  const hasOrderId = Boolean(order.shiprocketOrderId);
  const hasAwb = Boolean(order.awbCode && order.awbCode.trim().length > 0);
  const isPickupScheduled = (order.shippingStatus || '').toUpperCase().includes('PICKUP') || (order.pickupStatus || '').toUpperCase().includes('SCHEDULED');

  // 1. Create Shipment
  let canCreateShipment: ActionResult = { allowed: false };
  if (isOrderCancelled) {
    canCreateShipment = { allowed: false, reason: 'Order has been cancelled' };
  } else if (hasShipmentId && !isShipmentCancelled) {
    canCreateShipment = { allowed: false, reason: 'An active shipment already exists for this order' };
  } else if (isShipmentCancelled) {
    canCreateShipment = { allowed: true, reason: 'Re-create a new shipment for this active order' };
  } else {
    canCreateShipment = { allowed: true };
  }

  // 2. Assign AWB / Courier
  let canAssignAwb: ActionResult = { allowed: false };
  if (isCancelled) {
    canAssignAwb = { allowed: false, reason: 'Shipment has been cancelled' };
  } else if (isDelivered) {
    canAssignAwb = { allowed: false, reason: 'Order has already been delivered' };
  } else if (!hasShipmentId) {
    canAssignAwb = { allowed: false, reason: 'Shipment must be created first' };
  } else if (hasAwb) {
    canAssignAwb = { allowed: false, reason: `AWB already assigned (${order.awbCode})` };
  } else {
    canAssignAwb = { allowed: true };
  }

  // 3. Schedule Pickup
  let canSchedulePickup: ActionResult = { allowed: false };
  if (isCancelled) {
    canSchedulePickup = { allowed: false, reason: 'Shipment has been cancelled' };
  } else if (isDelivered) {
    canSchedulePickup = { allowed: false, reason: 'Order has already been delivered' };
  } else if (!hasShipmentId) {
    canSchedulePickup = { allowed: false, reason: 'Shipment must be created first' };
  } else if (!hasAwb) {
    canSchedulePickup = { allowed: false, reason: 'AWB must be assigned before scheduling pickup' };
  } else if (isPickupScheduled) {
    canSchedulePickup = { allowed: false, reason: 'Pickup has already been scheduled' };
  } else {
    canSchedulePickup = { allowed: true };
  }

  // 4. Cancel Shipment
  let canCancelShipment: ActionResult = { allowed: false };
  if (isCancelled) {
    canCancelShipment = { allowed: false, reason: 'Shipment is already cancelled' };
  } else if (isDelivered) {
    canCancelShipment = { allowed: false, reason: 'Delivered orders cannot be cancelled via shipment management' };
  } else if (!hasOrderId && !hasShipmentId) {
    canCancelShipment = { allowed: false, reason: 'No active shipment exists to cancel' };
  } else {
    canCancelShipment = { allowed: true };
  }

  // 5. Track Shipment
  let canTrackShipment: ActionResult = { allowed: false };
  if (!hasAwb) {
    canTrackShipment = { allowed: false, reason: 'No AWB assigned to track' };
  } else if (isCancelled && !hasAwb) {
    canTrackShipment = { allowed: false, reason: 'Shipment is cancelled and has no tracking' };
  } else {
    canTrackShipment = { allowed: true };
  }

  // 6. Download Label
  let canDownloadLabel: ActionResult = { allowed: false };
  if (isCancelled) {
    canDownloadLabel = { allowed: false, reason: 'Cannot generate label for a cancelled shipment' };
  } else if (!hasShipmentId) {
    canDownloadLabel = { allowed: false, reason: 'Shipment must be created first' };
  } else {
    canDownloadLabel = { allowed: true };
  }

  // 7. Download Invoice
  let canDownloadInvoice: ActionResult = { allowed: false };
  if (isCancelled) {
    canDownloadInvoice = { allowed: false, reason: 'Cannot generate invoice for a cancelled shipment' };
  } else if (!hasOrderId) {
    canDownloadInvoice = { allowed: false, reason: 'Shiprocket order must be created first' };
  } else {
    canDownloadInvoice = { allowed: true };
  }

  // 8. Run Full Auto-Fulfillment
  let canFullFulfill: ActionResult = { allowed: false };
  if (isCancelled) {
    canFullFulfill = { allowed: false, reason: 'Cannot run auto-fulfillment on a cancelled order' };
  } else if (isDelivered) {
    canFullFulfill = { allowed: false, reason: 'Order is already delivered' };
  } else {
    canFullFulfill = { allowed: true };
  }

  return {
    canCreateShipment,
    canAssignAwb,
    canSchedulePickup,
    canCancelShipment,
    canTrackShipment,
    canDownloadLabel,
    canDownloadInvoice,
    canFullFulfill,
  };
}

export function useShipmentActions(order: OrderForShipmentActions | null | undefined): ShipmentActionsMatrix {
  return getShipmentActions(order);
}
